/* eslint-disable max-len */
/* eslint-disable max-statements */

import { toast } from 'react-toastify';

import { GetMadbValuesCommand, GetMadbValuesResult, GetTaricDutyRatesCommand, TaricDescriptionResult, TaricDutyRatesResult } from '@e-origin/contracts';
import {
  CommodityCode,
  DeclarationGoods,
  EvalMethodAiHsCode,
  EvalMethodDescription,
  EvalMethodStatisticalValue,
  EvalMethodWebValue,
  IRiskAnalysisUpdateFields,
  ISaveNotes,
  WebScraping,
} from '@e-origin/shared';

import { RISK_HS_CODE_CUSTOMS_SYSTEM_LENGTH, RISK_HS_CODE_TARIC_CODE_LENGTH } from '@constants';
import { request } from '@utils';

class RiskAnalysisService {
  public async retrieveDutyRateAndMadbValue(hsCode: string): Promise<{ dutyRate: string; madbValue: GetMadbValuesResult } | null> {
    try {
      const commodityCode = this.extractDeclarationCommodityCode(hsCode, true);

      const dutyRateParams: GetTaricDutyRatesCommand = {
        harmonizedSystem: commodityCode.hsCodeCustomsSystem,
        nomenclatureCode: commodityCode.combinedNomenclatureCode,
        taricCode: commodityCode.taricCode,
      };
      const dutyRate: TaricDutyRatesResult = await request({
        path: `external-api/taric-duty-rates?harmonizedSystem=${dutyRateParams.harmonizedSystem}&nomenclatureCode=${dutyRateParams.nomenclatureCode}&taricCode=${dutyRateParams.taricCode}`,
        method: 'GET',
        authenticate: true,
      });

      const madbValueParams: GetMadbValuesCommand = {
        productNc: `${commodityCode.hsCodeCustomsSystem}${commodityCode.combinedNomenclatureCode}`,
        partnerIso: 'CN',
      };
      const madbValue: GetMadbValuesResult = await request({
        path: `external-api/madb-values?productNc=${madbValueParams.productNc}&partnerIso=${madbValueParams.partnerIso}`,
        method: 'GET',
        authenticate: true,
      });
      return { dutyRate: dutyRate[0], madbValue };
    } catch (error) {
      toast.error(`Couldn't extract the duty rate`);
      return null;
    }
  }

  public async retrieveTaricDescriptions(hsCode: string): Promise<TaricDescriptionResult | null> {
    try {
      return await request({
        path: `external-api/taric-description?harmonizedSystem=${hsCode}`,
        method: 'GET',
        authenticate: true,
      });
    } catch (error) {
      toast.error(`Couldn't extract the taric descriptions`);
      return null;
    }
  }

  public async saveNotes(goodsItemsIds: string[], data: ISaveNotes): Promise<void> {
    try {
      await request({
        path: 'declarations/save-notes',
        method: 'POST',
        authenticate: true,
        dataObject: {
          goodsItemsIds,
          data,
        },
      });
      toast.success('Notes were successfully saved');
    } catch (error) {
      toast.error(`Couldn't save the notes`);
    }
  }

  public evalMethodDescriptionCheckDisabled(goodDetails: DeclarationGoods): boolean {
    return (
      goodDetails.evalMethodDescription?.webMatchingDescription &&
      !goodDetails.evalMethodDescription?.suggestedDescriptionOfGoods &&
      !goodDetails.evalMethodDescription?.validatedDescriptionCheck
    );
  }

  public evalMethodAiHsCodeCheckDisabled(goodDetails: DeclarationGoods): boolean {
    return (
      goodDetails.evalMethodAiHsCode?.hsCodeOk && !goodDetails.evalMethodAiHsCode?.suggestedHsCode && !goodDetails.evalMethodAiHsCode?.validatedHSCodeCheck
    );
  }

  public evalMethodWebValueCheckDisabled(goodDetails: DeclarationGoods): boolean {
    return goodDetails.invoicedRateOk && !goodDetails.evalMethodWebValue?.validatedValueCheck;
  }

  public computeUpdatedDeclarationGoods(customerMinimumRate: number, goodDetails: DeclarationGoods, newValues: IRiskAnalysisUpdateFields): DeclarationGoods {
    let updatedGoodDetails: DeclarationGoods = {
      ...goodDetails,
      evalMethodDescription: this.computeEvalMethodDescription(goodDetails, newValues),
      evalMethodAiHsCode: this.computeEvalMethodAiHsCode(goodDetails, newValues),
      evalMethodWebValue: this.computeEvalMethodWebValue(goodDetails, newValues),
      evalMethodStatisticalValue: this.computeEvalMethodStatisticalValue(goodDetails, newValues, customerMinimumRate),
      webScraping: this.computeWebScraping(goodDetails, newValues),
    };

    updatedGoodDetails = {
      ...updatedGoodDetails,
      ...this.computeRootProperties(updatedGoodDetails),
    };

    return updatedGoodDetails;
  }

  private computeEvalMethodDescription(values: DeclarationGoods, newValues: IRiskAnalysisUpdateFields): EvalMethodDescription {
    if (newValues.validatedDescriptionCheckDisabled === undefined) {
      return values.evalMethodDescription;
    }

    return {
      ...values.evalMethodDescription,
      suggestedDescriptionOfGoods: !newValues?.validatedDescriptionCheck ? newValues?.newDescription : '',
      validatedDescriptionCheck: newValues?.validatedDescriptionCheck,
      webMatchingDescription: newValues?.validatedDescriptionCheck || !!newValues?.newDescription || newValues?.validatedDescriptionCheckDisabled,
    };
  }

  private computeEvalMethodAiHsCode(values: DeclarationGoods, newValues: IRiskAnalysisUpdateFields): EvalMethodAiHsCode {
    if (newValues.validatedHSCodeCheckDisabled === undefined) {
      return values.evalMethodAiHsCode;
    }

    return {
      ...values.evalMethodAiHsCode,
      suggestedHsCode: !newValues?.validatedHSCodeCheck ? newValues?.newHsCodeDescription : '',
      hsCodeOk: newValues?.validatedHSCodeCheck || !!newValues?.newHsCodeDescription || newValues?.validatedHSCodeCheckDisabled,
      validatedHSCodeCheck: newValues?.validatedHSCodeCheck,
      annotationNote: newValues?.annotationNote,
      annotationCustomsDecision: newValues?.annotationCustomsDecision,
      suggestedTaricRate: newValues?.newHsCodeDescriptionDutyRate,
    };
  }

  private computeEvalMethodWebValue(values: DeclarationGoods, newValues: IRiskAnalysisUpdateFields): EvalMethodWebValue {
    if (newValues.validatedValuesCheckDisabled === undefined) {
      return values.evalMethodWebValue;
    }

    const invoicedRateOk =
      newValues.invoicedRateOk && values.evalMethodWebValue.validatedValueCheck && !newValues?.validatedValueCheck && !newValues?.validatedValuesCheckDisabled
        ? false
        : newValues?.validatedValueCheck || (newValues.newPriceSaved >= 0 ? newValues.invoicedRateOk : values.evalMethodWebValue.invoicedRateOk);

    return {
      ...values.evalMethodWebValue,
      invoicedRate: newValues.newPriceSaved ? newValues.invoicedRate : values.evalMethodWebValue.invoicedRate,
      invoicedRateOk,
      validatedValueCheck: newValues?.validatedValueCheck,
    };
  }

  private computeEvalMethodStatisticalValue(
    values: DeclarationGoods,
    newValues: IRiskAnalysisUpdateFields,
    customerMinimumRate: number,
  ): EvalMethodStatisticalValue {
    if (newValues.validatedValuesCheckDisabled === undefined) {
      return values.evalMethodStatisticalValue;
    }

    const evalMethodStatisticalValue = {
      ...values.evalMethodStatisticalValue,
      validatedStatisticalValue: newValues?.validatedValueCheck,
    };
    const madbInvoicedRateOk = (rate) => {
      return newValues?.validatedValueCheck || rate > (customerMinimumRate || 1);
    };
    if (evalMethodStatisticalValue.lowest) {
      evalMethodStatisticalValue.lowest = {
        ...evalMethodStatisticalValue.lowest,
        madbInvoicedRateOk: madbInvoicedRateOk(evalMethodStatisticalValue.lowest.madbInvoicedRate),
      };

      if (
        ![null, undefined].includes(newValues.nowMadbValue) &&
        !!newValues.newHsCodeDescription &&
        newValues.newHsCodeDescription === values.evalMethodAiHsCode.suggestedHsCode
      ) {
        const madbInvoicedRate = values.evalMethodWebValue.invoicedAmountPerKg / newValues.nowMadbValue;
        evalMethodStatisticalValue.suggestedLowest = {
          ...evalMethodStatisticalValue.suggestedLowest,
          madbAmountPerKg: newValues.nowMadbValue,
          madbInvoicedRate,
          madbInvoicedRateOk: madbInvoicedRateOk(madbInvoicedRate),
        };
      } else {
        evalMethodStatisticalValue.suggestedLowest = null;
      }
    }
    if (evalMethodStatisticalValue.highest) {
      evalMethodStatisticalValue.highest = {
        ...evalMethodStatisticalValue.highest,
        madbInvoicedRateOk: madbInvoicedRateOk(evalMethodStatisticalValue.highest.madbInvoicedRate),
      };
    }
    return evalMethodStatisticalValue;
  }

  private computeWebScraping(values: DeclarationGoods, newValues: IRiskAnalysisUpdateFields): WebScraping {
    return {
      ...values.webScraping,
      webItemAmountResale: newValues.newPriceSaved || values.webScraping.webItemAmountResale || 0,
    };
  }

  private computeRootProperties(values: DeclarationGoods): Partial<DeclarationGoods> {
    return {
      invoicedRateOk: !!(
        values.evalMethodWebValue?.validatedValueCheck ||
        values.evalMethodWebValue?.invoicedRateOk ||
        (((!values.evalMethodStatisticalValue?.suggestedLowest && values.evalMethodStatisticalValue?.lowest?.madbInvoicedRateOk) ||
          values.evalMethodStatisticalValue?.suggestedLowest?.madbInvoicedRateOk) &&
          values.webScraping.webItemAmountResale === 0)
      ),
    };
  }

  private extractDeclarationCommodityCode(hsCode: string, isHighValueDeclaration?: boolean): CommodityCode {
    const commodityCode: CommodityCode = {
      hsCode,
      hsCodeCustomsSystem: hsCode.substring(0, RISK_HS_CODE_CUSTOMS_SYSTEM_LENGTH),
    };

    if (isHighValueDeclaration) {
      let remainingHsCode = hsCode.slice(RISK_HS_CODE_CUSTOMS_SYSTEM_LENGTH);
      if (remainingHsCode.length >= RISK_HS_CODE_TARIC_CODE_LENGTH) {
        commodityCode.combinedNomenclatureCode = remainingHsCode.substring(0, RISK_HS_CODE_TARIC_CODE_LENGTH);
      }
      remainingHsCode = remainingHsCode.slice(RISK_HS_CODE_TARIC_CODE_LENGTH);
      if (remainingHsCode.length >= RISK_HS_CODE_TARIC_CODE_LENGTH) {
        commodityCode.taricCode = remainingHsCode.substring(0, RISK_HS_CODE_TARIC_CODE_LENGTH);
      } else {
        // todo: there is a similar method in Backend but this line is different
        commodityCode.taricCode = '00';
      }

      remainingHsCode = remainingHsCode.slice(RISK_HS_CODE_TARIC_CODE_LENGTH);
      if (remainingHsCode.length >= RISK_HS_CODE_TARIC_CODE_LENGTH) {
        commodityCode.taricAdditionalCode = remainingHsCode.substring(0, RISK_HS_CODE_TARIC_CODE_LENGTH);
      }
      remainingHsCode = remainingHsCode.slice(RISK_HS_CODE_TARIC_CODE_LENGTH);
      if (remainingHsCode.length >= RISK_HS_CODE_TARIC_CODE_LENGTH) {
        commodityCode.nationalAdditionalCode = remainingHsCode.substring(0, RISK_HS_CODE_TARIC_CODE_LENGTH);
      }
    }
    return commodityCode;
  }
}

export const riskAnalysisService = new RiskAnalysisService();
